import apiClient from './client';
import type { SupportedCurrency } from '../store/slices/currencySlice';
import type { SourceProofMetadata } from '../types/sourceProof';

export interface DisplayCurrencyMetadata {
  effectiveDisplayCurrency?: SupportedCurrency;
  currencySource?: string;
  monetaryBaseCurrency?: string;
  rawValueMarker?: string;
  displayCurrencyConversionAvailable?: boolean;
  displayCurrencyConversionRate?: number | null;
}

export interface PortfolioSnapshot {
  timestamp: string;
  total_value_usd: number;
  asset_count?: number;
  wallet_count?: number;
  defi_value_usd?: number;
  wallet_value_usd?: number;
  snapshot_type?: string;
  assets?: Array<{
    symbol: string;
    valueUsd: number;
    amount: number;
  }>;
}

export interface PortfolioHistoryResponse {
  userId: string;
  interval: 'hourly' | 'daily';
  from: string;
  to: string;
  count: number;
  snapshots: PortfolioSnapshot[];
}

type RawPortfolioSnapshot = PortfolioSnapshot & {
  avg_value_usd?: number | string;
  min_value_usd?: number | string;
  max_value_usd?: number | string;
  open_value_usd?: number | string;
  close_value_usd?: number | string;
  snapshot_count?: number;
};

function toNumericValue(value: number | string | undefined): number | undefined {
  if (typeof value === 'number') {
    return Number.isFinite(value) ? value : undefined;
  }

  if (typeof value === 'string' && value.trim() !== '') {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : undefined;
  }

  return undefined;
}

function normalisePortfolioSnapshot(snapshot: RawPortfolioSnapshot): PortfolioSnapshot {
  return {
    ...snapshot,
    total_value_usd:
      toNumericValue(snapshot.total_value_usd) ??
      toNumericValue(snapshot.close_value_usd) ??
      toNumericValue(snapshot.avg_value_usd) ??
      toNumericValue(snapshot.open_value_usd) ??
      0,
  };
}

export interface AssetAttribution {
  symbol: string;
  contribution: number; // Contribution to total return (%)
  return: number; // Asset-level return (%)
  startValue: number;
  endValue: number;
  weight: number; // Average weight in portfolio (%)
}

export interface PerformanceResult {
  userId: string;
  from: string;
  to: string;
  twr: number; // Time-Weighted Return (%)
  mwr: number; // Money-Weighted Return (%)
  totalReturn: number; // Simple return (%)
  startValue: number;
  endValue: number;
  netCashFlow: number;
  attribution: AssetAttribution[];
  sharpeRatio: number | null; // Risk-adjusted return (annualized)
  maxDrawdown: number | null; // Maximum peak-to-trough decline (%)
}

/**
 * Get portfolio historical snapshots
 * @param userId - User ID
 * @param from - Start date (optional, defaults to 30 days ago)
 * @param to - End date (optional, defaults to now)
 * @param interval - 'hourly' or 'daily' (auto-selected based on date range)
 */
export async function getPortfolioHistory(
  userId: string,
  from?: Date | string,
  to?: Date | string,
  interval?: 'hourly' | 'daily'
): Promise<PortfolioHistoryResponse> {
  const params = new URLSearchParams();
  if (from) params.append('from', typeof from === 'string' ? from : from.toISOString());
  if (to) params.append('to', typeof to === 'string' ? to : to.toISOString());
  if (interval) params.append('interval', interval);

  const url = `/analytics/portfolio/history/${userId}${params.toString() ? `?${params.toString()}` : ''}`;
  const res = await apiClient.get<Omit<PortfolioHistoryResponse, 'snapshots'> & { snapshots: RawPortfolioSnapshot[] }>(url);
  return {
    ...res.data,
    snapshots: (res.data.snapshots || []).map(normalisePortfolioSnapshot),
  };
}

/**
 * Get portfolio performance metrics (TWR, MWR, Attribution)
 * @param userId - User ID
 * @param from - Start date (optional, defaults to 30 days ago)
 * @param to - End date (optional, defaults to now)
 */
export async function getPortfolioPerformance(
  userId: string,
  from?: Date | string,
  to?: Date | string
): Promise<PerformanceResult> {
  const params = new URLSearchParams();
  if (from) params.append('from', typeof from === 'string' ? from : from.toISOString());
  if (to) params.append('to', typeof to === 'string' ? to : to.toISOString());

  const url = `/analytics/portfolio/performance/${userId}${params.toString() ? `?${params.toString()}` : ''}`;
  const res = await apiClient.get<PerformanceResult>(url);
  return res.data;
}

export interface VolatilityMetrics {
  userId: string;
  daily: number; // Daily volatility (%)
  annualized: number; // Annualized volatility (%)
  rolling30Day: number; // 30-day rolling volatility (%)
  rolling90Day: number; // 90-day rolling volatility (%)
  trend: 'increasing' | 'decreasing' | 'stable';
}

export interface VaRMetrics {
  userId: string;
  var95: number; // 95% confidence VaR (%)
  var99: number; // 99% confidence VaR (%)
  var90: number; // 90% confidence VaR (%)
  expectedShortfall: number; // Conditional VaR (CVaR) at 95%
  currentValue: number; // Current portfolio value
  var95Dollar: number; // 95% VaR in dollar terms
  var99Dollar: number; // 99% VaR in dollar terms
}

/**
 * Get portfolio volatility metrics
 * @param userId - User ID
 */
export async function getPortfolioVolatility(userId: string): Promise<VolatilityMetrics> {
  const url = `/analytics/portfolio/volatility/${userId}`;
  const res = await apiClient.get<VolatilityMetrics>(url);
  return res.data;
}

/**
 * Get portfolio Value at Risk (VaR) metrics
 * @param userId - User ID
 */
export async function getPortfolioVaR(userId: string): Promise<VaRMetrics> {
  const url = `/analytics/portfolio/var/${userId}`;
  const res = await apiClient.get<VaRMetrics>(url);
  return res.data;
}

export interface CorrelationPair {
  asset1: string;
  asset2: string;
  correlation: number;
}

export interface CorrelationMatrix {
  symbols: string[];
  matrix: number[][]; // Correlation coefficients
  pairs: CorrelationPair[];
}

export interface DiversificationMetrics {
  hhi: number; // Herfindahl-Hirschman Index (0-10000, lower = more diversified)
  effectiveAssetCount: number; // Equivalent number of equally-weighted assets
  concentrationRatio: number; // Top 3 assets as % of portfolio (0-100)
  diversificationScore: number; // 0-100 (higher = more diversified)
  recommendations: string[];
}

export interface PortfolioCorrelationResult {
  userId: string;
  period: 'monthly' | 'quarterly' | 'yearly';
  correlationMatrix: CorrelationMatrix;
  diversification: DiversificationMetrics;
}

/**
 * Get portfolio correlation and diversification analysis
 * @param userId - User ID
 */
export async function getPortfolioCorrelation(userId: string): Promise<PortfolioCorrelationResult> {
  const url = `/analytics/portfolio/correlation/${userId}`;
  const res = await apiClient.get<PortfolioCorrelationResult>(url);
  return res.data;
}

export interface SectorExposure {
  sector: string;
  valueUsd: number;
  weightPct: number;
  assetCount: number;
  hhi: number;
  displayValue?: number | null;
}

export interface ChainExposure {
  chain: string;
  valueUsd: number;
  weightPct: number;
  assetCount: number;
  displayValue?: number | null;
}

export interface ProtocolExposure {
  protocol: string;
  valueUsd: number;
  weightPct: number;
  assetCount: number;
  displayValue?: number | null;
}

export interface ConcentrationAnalysis extends DisplayCurrencyMetadata {
  userId: string;
  totalValue: number;
  sectorExposure: SectorExposure[];
  chainExposure: ChainExposure[];
  protocolExposure: ProtocolExposure[];
  alerts: string[];
  recommendations: string[];
  displayExposure?: {
    sectorExposure: SectorExposure[];
    chainExposure: ChainExposure[];
    protocolExposure: ProtocolExposure[];
  };
  sourceProof?: SourceProofMetadata;
}

/**
 * Get portfolio concentration analysis (sectors, chains, protocols)
 * @param userId - User ID
 */
export async function getPortfolioConcentration(userId: string): Promise<ConcentrationAnalysis> {
  const url = `/analytics/portfolio/concentration/${userId}`;
  const res = await apiClient.get<ConcentrationAnalysis>(url);
  return res.data;
}

export interface ScenarioResult {
  portfolioValueBefore: number;
  portfolioValueAfter: number;
  totalChangeDollars: number;
  totalChangePercent: number;
  assetChanges: Array<{
    symbol: string;
    amountHeld: number;
    priceBeforeUsd: number;
    priceAfterUsd: number;
    valueBefore: number;
    valueAfter: number;
    changeDollars: number;
    changePercent: number;
  }>;
}

export interface StressTestScenario {
  name: string;
  description: string;
  impactPercentage: number;
  impactDollars: number;
  valueAfter: number;
  displayImpactDollars?: number | null;
  displayValueAfter?: number | null;
}

export interface SeverityRanking {
  scenario: string;
  portfolioImpactPercent: number;
  severity: 'low' | 'medium' | 'high' | 'critical';
}

export interface ScenarioAnalysisResponse extends DisplayCurrencyMetadata {
  userId: string;
  currentPortfolioValue: number;
  timestamp: string;
  scenarios: StressTestScenario[];
  severityRanking: SeverityRanking[];
  displayScenarios?: StressTestScenario[];
  sourceProof?: SourceProofMetadata;
}

/**
 * Get scenario and stress test analysis
 * @param userId - User ID
 */
export async function getPortfolioScenarios(userId: string): Promise<ScenarioAnalysisResponse> {
  const url = `/analytics/portfolio/scenarios/${userId}`;
  const res = await apiClient.get<ScenarioAnalysisResponse>(url);
  return res.data;
}

export interface PriceChangeScenario {
  symbol: string;
  priceChangePercent: number;
}

/**
 * Analyze a custom scenario with specific price changes
 * @param userId - User ID
 * @param priceChanges - Array of price change scenarios
 */
export async function analyzeCustomScenario(
  userId: string,
  priceChanges: PriceChangeScenario[]
): Promise<ScenarioResult> {
  const url = `/analytics/portfolio/scenarios/${userId}/custom`;
  const res = await apiClient.post<ScenarioResult>(url, { priceChanges });
  return res.data;
}

export interface BenchmarkData {
  range: string;
  btc: number | null;
  eth: number | null;
  generatedAt: string;
}

/**
 * Get market benchmark performance (BTC and ETH % change) for a given time range
 * @param range - Time range: '7d' | '30d' | '90d' | '1y'
 */
export async function getBenchmarks(range: string): Promise<BenchmarkData> {
  const url = `/analytics/benchmarks?range=${range}`;
  const res = await apiClient.get<BenchmarkData>(url);
  return res.data;
}

export interface AssetScore {
  symbol: string;
  name: string;
  currentWeight: number;
  recommendedWeight: number;
  diversificationRating: number; // 0-100
  volatilityRating: number; // 0-100
  riskAdjustedReturnRating: number; // 0-100
  correlationWithPortfolio: number; // -1 to 1
  overallScore: number; // 0-100
  recommendation: 'strong_buy' | 'buy' | 'hold' | 'sell' | 'strong_sell';
  reasoning: string[];
}

export interface PortfolioOptimization {
  userId: string;
  currentPortfolioValue: number;
  timestamp: string;
  suggestions: AssetScore[];
  topRecommendations: AssetScore[];
  underweightedAssets: AssetScore[];
  overweightedAssets: AssetScore[];
  overallPortfolioScore: number;
  optimizationPotential: number; // 0-100
  sourceProof?: SourceProofMetadata;
}

/**
 * Get portfolio optimization suggestions
 * @param userId - User ID
 */
export async function getPortfolioOptimization(userId: string): Promise<PortfolioOptimization> {
  const url = `/analytics/portfolio/optimization/${userId}`;
  const res = await apiClient.get<PortfolioOptimization>(url);
  return res.data;
}
export interface RiskMetricsData {
  userId: string;
  range: string;
  from: string;
  to: string;
  sharpeRatio: number | null;
  sortinoRatio: number | null;
  maxDrawdown: number | null;
  calmarRatio: number | null;
}

/**
 * Get risk-adjusted performance metrics
 * @param userId - User ID
 * @param range - Time range: 30d | 90d | 1y
 */
export async function getRiskMetrics(userId: string, range: string): Promise<RiskMetricsData> {
  const url = `/analytics/risk-metrics/${userId}?range=${range}`;
  const res = await apiClient.get<RiskMetricsData>(url);
  return res.data;
}

// ── Simulation (Chunk 74) ────────────────────────────────────────────────────

export interface SimulatedAsset {
  symbol: string;
  currentValueUsd: number;
  simulatedValueUsd: number;
  currentAllocationPct: number;
  simulatedAllocationPct: number;
  priceShockApplied: number;
  liquidationRiskFlag: boolean;
}

export interface SimulationResult {
  userId: string;
  timestamp: string;
  currentTotalValueUsd: number;
  simulatedTotalValueUsd: number;
  totalChangeDollars: number;
  totalChangePercent: number;
  sharpeRatioDelta: number | null;
  liquidationRiskAssets: string[];
  assets: SimulatedAsset[];
  dataQuality: 'complete' | 'insufficient' | 'unavailable';
  error?: string;
}

export interface SimulationParams {
  priceShocks?: Record<string, number>;
  additionalPositions?: Array<{ symbol: string; amountUsd: number }>;
  removedPositions?: string[];
}

export async function simulatePortfolio(userId: string, params: SimulationParams): Promise<SimulationResult> {
  const query = new URLSearchParams();
  if (params.priceShocks && Object.keys(params.priceShocks).length > 0) {
    query.set('priceShocks', JSON.stringify(params.priceShocks));
  }
  if (params.additionalPositions && params.additionalPositions.length > 0) {
    query.set('additionalPositions', JSON.stringify(params.additionalPositions));
  }
  if (params.removedPositions && params.removedPositions.length > 0) {
    query.set('removedPositions', JSON.stringify(params.removedPositions));
  }
  const url = `/analytics/simulate/${userId}?${query.toString()}`;
  const res = await apiClient.get<SimulationResult>(url);
  return res.data;
}
